import React, { useEffect, useMemo, useRef, useCallback, useState, useContext } from "react";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import moment from "moment";
import _ from "lodash";

import COMMON from "common";
import api from "services/api";
import classNames from "common/class-names";
import serveRequestErrors from "common/serve-request-errors";
import { AxiosContext } from "contexts/with-interceptor-provider";
import { formatDatePattern, CALENDAR_FORMAT, getISOString, getDates, isSameDate, nthDate, getWeekdays } from "common/calendar";
import useLeaveRegionFilter from "hooks/use-leave-region-filter";
import AppButton from "components/app-button";
import AppMultiSelectInput from "components/app-multi-select-input";
import AppChevronIcon from "components/icons/app-chevron-icon";
import AppRefreshLeaveModal from "components/pages/leave-management/app-refresh-leave-modal";
import resetIcon from "assets/images/pages/leave-management/reset-icon.svg";
import listingIcon from "assets/images/pages/leave-management/listing-icon.svg";
import infoIcon from "assets/images/question-mark-icon.svg";
import compassionateLeaveIcon from "assets/images/pages/leave-management/compassionate-leave-icon.svg";
import otherLeaveIcon from "assets/images/pages/leave-management/other-leave-icon.svg";
import maternityLeaveIcon from "assets/images/pages/leave-management/maternity-leave-icon.svg";
import annualLeaveIcon from "assets/images/pages/leave-management/annual-leave-icon.svg";
import annualLeaveAMIcon from "assets/images/pages/leave-management/annual-leave-icon-AM.svg";
import annualLeavePMIcon from "assets/images/pages/leave-management/annual-leave-icon-PM.svg";
import medicalLeaveIcon from "assets/images/pages/leave-management/medical-leave-icon.svg";
import medicalLeaveAMIcon from "assets/images/pages/leave-management/medical-leave-icon-AM.svg";
import medicalLeavePMIcon from "assets/images/pages/leave-management/medical-leave-icon-PM.svg";
import holidayIcon from "assets/images/holiday-icon.svg";

const currentDateFormat = CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const nthDateLabel = (d) => new Date(d).getDate() + nthDate(new Date(d).getDate());

// The key mapping is name|||type|||timeOffType
// This will verify if key is holiday
const isHolidayKey = (key) => key.split("|||")[1] === "holiday";

// To shows "5th - 7th" when endDate differs
const getHolidayDateLabel = (holiday) => {
	if (!holiday?.endDate || isSameDate(holiday.startDate, holiday.endDate)) return nthDateLabel(holiday.startDate);

	return nthDateLabel(holiday.startDate) + " - " + nthDateLabel(holiday.endDate);
};

const AppGridCalendar = (props) => {
	const refreshLeaveRef = useRef();

	const context = useContext(AxiosContext);

	const calendarGridRef = useRef(null);

	const leavesListRef = useRef(null);

	const [selectedHoliday, setSelectedHoliday] = useState(null);

	const [date, setDate] = useState(props.currentDate);

	const [onLeaves, setOnLeaves] = useState([]);

	const [calendar, setCalendar] = useState({});

	const [rawLeaves, setRawLeaves] = useState([]);

	const [anchorE2, setAnchorE2] = useState(null);

	const { regions, regionOptions, holiday, filterLeaves, onChangeRegion } = useLeaveRegionFilter(date.getFullYear());

	const filteredLeaves = useMemo(() => filterLeaves(rawLeaves), [filterLeaves, rawLeaves]);

	const currentDate = useRef(date);

	const dates = useMemo(() => getDates(date), [date]);

	const weekdays = useMemo(() => getWeekdays(CALENDAR_FORMAT.WEEKDAYS_FORMAT), []);

	const sanitizeCalendar = useCallback((response) => {
		const leaves = {};

		getDates(currentDate.current).forEach((a) => {
			response.forEach((b) => {
				const isSame = isSameDate(b.date, a.iso);

				if (isSame) {
					const date = leaves[a.iso];
					const leaveType = b.leaveTypeMapped;
					const timeOffType = b.timeOffType;

					if (date) {
						if (date[leaveType]) {
							if (date[leaveType][timeOffType]) {
								const timeOffTypes = date[leaveType][timeOffType];
								leaves[a.iso][leaveType][timeOffType] = [...timeOffTypes, b];
							} else {
								leaves[a.iso][leaveType] = {
									...date[leaveType],
									[timeOffType]: [b],
								};
							}
						} else {
							leaves[a.iso] = {
								...date,
								[leaveType]: { [timeOffType]: [b] },
							};
						}
					} else {
						leaves[a.iso] = {};
						leaves[a.iso][leaveType] = {};
						leaves[a.iso][leaveType][timeOffType] = [b];
					}
				}
			});
		});

		setCalendar(leaves);
	}, []);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		try {
			const payload = { month: currentDate.current.getMonth() + 1, year: currentDate.current.getFullYear() };

			response = await api.get.leaves.calendar(payload);
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) setRawLeaves(response);
	}, []);

	const onDebounceChangeDate = useCallback(
		_.debounce(() => {
			onHandleGetList();
		}, 1000),
		[onHandleGetList]
	);
	const onHandleChangeDate = (event) => {
		const action = event.target.getAttribute("data-target");

		const nextDate = new Date(currentDate.current);

		nextDate.setDate(1);

		if (action === "data-prev") nextDate.setMonth(nextDate.getMonth() - 1);
		if (action === "data-next") nextDate.setMonth(nextDate.getMonth() + 1);

		setDate(nextDate);

		currentDate.current = nextDate;

		onDebounceChangeDate();
	};

	const onHandleSetToday = () => {
		const today = new Date();

		setDate(today);

		currentDate.current = today;

		onDebounceChangeDate();
	};

	const onhandleCloseInfoMenu = useCallback(() => {
		setAnchorE2(null);
	}, []);

	const onHandleShowEmployeeAppliedLeave = useCallback(
		(event, employeeLeaveApplied) => {
			/* TO BE Enhance */

			setOnLeaves(null);
			const selectedDate = event.currentTarget.getAttribute("data-type");

			event.stopPropagation();

			if (employeeLeaveApplied) {
				const targetedItems = Object.keys(calendar)
					.map((o) => (isSameDate(o, selectedDate) ? calendar[o] : null))
					.filter(Boolean);

				let flattenItems = Object.keys(targetedItems).map((a) => targetedItems[a]);
				/* Flatten Leave Types */
				flattenItems = flattenItems.map((type) => Object.keys(type).map((a) => type[a])).flat();
				/* Flatten Leave hours */
				flattenItems = flattenItems
					.map((hour) =>
						Object.keys(hour)
							.map((a) => hour[a])
							.flat()
					)
					.flat();

				const users = flattenItems.map((item) => item.userEmail);

				const flattenUsers = [...new Set(users)];

				const leaves = {};

				/* Set all leaves ranges */
				flattenUsers.forEach((user) => {
					filteredLeaves.forEach((o) => {
						if (o.userEmail !== user) return;

						if (!leaves[user]) leaves[user] = {};

						if (!leaves[user][o.leaveType]) leaves[user][o.leaveType] = {};

						if (!leaves[user][o.leaveType][o.timeOffType]) leaves[user][o.leaveType][o.timeOffType] = [];

						leaves[user][o.leaveType][o.timeOffType].push(o);
					});

					Object.keys(leaves[user]).forEach((leaveType) => {
						Object.keys(leaves[user][leaveType]).forEach((timeOffType) => {
							let userLeaves = leaves[user][leaveType][timeOffType];

							const ranges = userLeaves
								.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
								.reduce(
									(acc, obj) => {
										const group = acc[acc.length - 1];

										if (moment(obj.date).diff(moment(group[group.length - 1]?.date || obj.date), "days") > 1) {
											acc.push([obj]);
										} else {
											group.push(obj);
										}

										return acc;
									},
									[[]]
								);

							const updatedLeaves = [];

							ranges.forEach((group) => {
								group.forEach((leaveObj) => {
									updatedLeaves.push({ ...leaveObj, ranges: group });
								});
							});

							leaves[user][leaveType][timeOffType] = updatedLeaves;
						});
					});
				});

				/* Set specify selected date ranges */
				flattenItems = flattenItems.map((a) => {
					let items = { ...a };
					const userEmail = a.userEmail;

					Object.keys(leaves[userEmail]).forEach((b) => {
						if (leaves[userEmail][b][a.timeOffType]) {
							const sameDate = leaves[userEmail][b][a.timeOffType].find((c) => c.date === a.date);

							if (sameDate) items.ranges = sameDate.ranges;
						}
					});

					return items;
				});

				setOnLeaves(flattenItems);
			}
		},
		[calendar, filteredLeaves]
	);

	const onHandleShowHolidayList = useCallback(
		(event, holidayEvent) => {
			event.stopPropagation();

			setSelectedHoliday(null);

			if (holidayEvent) {
				const selectedDate = event.currentTarget.getAttribute("data-type");
				const targetedItem = holiday[getISOString(selectedDate)];
				setSelectedHoliday(targetedItem ? [targetedItem] : null);
			}
		},
		[holiday]
	);

	const onHandleOpenInfoMenu = useCallback((event) => {
		setAnchorE2(event.target);
	}, []);

	const onHandleResetLeaves = () => {
		refreshLeaveRef.current.onHandleShow();
	};

	//prettier-ignore
	const getEmployeeLeaveApplied = useCallback((iso) => {
		return Object.keys(calendar).findIndex((date) => iso === date) > -1;
	}, [calendar]);

	const getHolidayAndEmployeeConsecutiveLeaveMap = () => {
		const holidayAndEmployeeLeaveMap = {};

		for (const date of dates) {
			const dayIso = date.iso;
			const holidayDate = holiday[dayIso];

			if (holidayDate != null) {
				const key = `${holidayDate.title}|||holiday|||`;

				if (!holidayAndEmployeeLeaveMap[key]) holidayAndEmployeeLeaveMap[key] = [];
				holidayAndEmployeeLeaveMap[key].push(dayIso);
			}

			if (!calendar[dayIso]) continue;

			Object.entries(calendar[dayIso]).forEach(([type, typeGroup]) => {
				Object.values(typeGroup).forEach((items) => {
					items.forEach(({ userFullName, timeOffType }) => {
						const key = `${userFullName}|||${type}|||${timeOffType}`;

						if (!holidayAndEmployeeLeaveMap[key]) holidayAndEmployeeLeaveMap[key] = [];
						holidayAndEmployeeLeaveMap[key].push(dayIso);
					});
				});
			});
		}

		// Group consecutive dates per employee-leaveType combo
		const groupedMap = {};
		for (const key in holidayAndEmployeeLeaveMap) {
			const sortedDates = holidayAndEmployeeLeaveMap[key].sort();
			const groups = [];
			let group = [];

			for (let i = 0; i < sortedDates.length; i++) {
				const current = new Date(sortedDates[i]);
				const next = new Date(sortedDates[i + 1]);
				group.push(sortedDates[i]);

				const isNextConsecutive = (() => {
					if (!next) return false;

					const dayDiff = (next.getTime() - current.getTime()) / (1000 * 60 * 60 * 24);
					if (dayDiff !== 1) return false;

					if (current.getDay() === 0) return false;

					return true;
				})();

				if (!isNextConsecutive) {
					groups.push(group);
					group = [];
				}
			}

			groupedMap[key] = groups;
		}

		return groupedMap;
	};

	const getLeaveRowMapping = (holidayAndEmployeeLeaveMap) => {
		const leaveBlockToRowMap = {};
		const rowAvailabilityByDate = {};

		for (const [name, groups] of Object.entries(holidayAndEmployeeLeaveMap)) {
			groups.forEach((group, groupIndex) => {
				const isHoliday = isHolidayKey(name);
				if (isHoliday) {
					const key = `${name}::${groupIndex}`;

					leaveBlockToRowMap[key] = 0;

					for (const date of group) {
						if (!rowAvailabilityByDate[date]) {
							rowAvailabilityByDate[date] = new Set();
						}
						rowAvailabilityByDate[date].add(0);
					}
				}
			});
		}

		for (const [name, groups] of Object.entries(holidayAndEmployeeLeaveMap)) {
			groups.forEach((group, groupIndex) => {
				if (isHolidayKey(name)) return; // Already placed

				const key = `${name}::${groupIndex}`;

				let row = 0;

				while (true) {
					const conflict = group.some((date) => rowAvailabilityByDate[date]?.has(row));

					if (!conflict) break;

					row++;
				}

				leaveBlockToRowMap[key] = row;

				for (const date of group) {
					if (!rowAvailabilityByDate[date]) {
						rowAvailabilityByDate[date] = new Set();
					}
					rowAvailabilityByDate[date].add(row);
				}
			});
		}

		return leaveBlockToRowMap;
	};

	const holidayAndEmployeeConsecutiveMap = getHolidayAndEmployeeConsecutiveLeaveMap();
	const leaveBlockToRowMap = getLeaveRowMapping(holidayAndEmployeeConsecutiveMap);

	const leaveIcons = {
		"Annual Leave": {
			"Full Day": annualLeaveIcon,
			"Half Day AM": annualLeaveAMIcon,
			"Half Day PM": annualLeavePMIcon,
		},
		"Compassionate Leave": {
			"Full Day": compassionateLeaveIcon,
			"Half Day AM": compassionateLeaveIcon,
			"Half Day PM": compassionateLeaveIcon,
		},
		"Maternity Leave": {
			"Full Day": maternityLeaveIcon,
			"Half Day AM": maternityLeaveIcon,
			"Half Day PM": maternityLeaveIcon,
		},
		"Paternity Leave": {
			"Full Day": maternityLeaveIcon,
			"Half Day AM": maternityLeaveIcon,
			"Half Day PM": maternityLeaveIcon,
		},
		"Sick Leave": {
			"Full Day": medicalLeaveIcon,
			"Half Day AM": medicalLeaveAMIcon,
			"Half Day PM": medicalLeavePMIcon,
		},
		"Hospitalisation Leave": {
			"Full Day": medicalLeaveIcon,
			"Half Day AM": medicalLeaveAMIcon,
			"Half Day PM": medicalLeavePMIcon,
		},
		"Other Leave": {
			"Full Day": otherLeaveIcon,
			"Half Day AM": otherLeaveIcon,
			"Half Day PM": otherLeaveIcon,
		},
	};

	const leaveMenuItems = [
		{ custom: true, className: "public-holiday-icon", label: "Public Holiday" },
		{ custom: true, className: "taken-leave-icon", label: "Taken Leave" },
		{ custom: true, className: "rest-day-icon", label: "Rest Day" },
		{ type: "Annual Leave", timeOff: "Full Day", label: "Annual Leave - Full Day" },
		{ type: "Annual Leave", timeOff: "Half Day AM", label: "Annual Leave - AM" },
		{ type: "Annual Leave", timeOff: "Half Day PM", label: "Annual Leave - PM" },
		{ type: "Sick Leave", timeOff: "Full Day", label: "Sick Leave - Full Day" },
		{ type: "Sick Leave", timeOff: "Half Day AM", label: "Sick Leave - AM" },
		{ type: "Sick Leave", timeOff: "Half Day PM", label: "Sick Leave - PM" },
		{ type: "Compassionate Leave", timeOff: "Full Day", label: "Compassionate Leave" },
		{ type: "Paternity Leave", timeOff: "Full Day", label: "Paternity Leave" },
		{ type: "Maternity Leave", timeOff: "Full Day", label: "Maternity Leave" },
		{ type: "Other Leave", timeOff: "Full Day", label: "Other Leave" },
	];

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		sanitizeCalendar(filteredLeaves);
		setOnLeaves([]);
	}, [filteredLeaves, sanitizeCalendar]);

	useEffect(() => {
		return () => {
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.LEAVES.CALENDAR);
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.HOLIDAY_CALENDAR.YEAR);
		};
	}, [context]);

	useEffect(() => {
		// Get the calendar and leaves elements
		const gridElement = calendarGridRef.current;
		const leavesElement = leavesListRef.current;

		if (!gridElement || !leavesElement) return;

		let rafId = null;

		// Limit leaves height to the calendar height
		const onHandleMeasure = (height) => {
			leavesElement.style.maxHeight = `${height}px`;
		};

		onHandleMeasure(gridElement.offsetHeight);

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];

			if (!entry) return;

			if (rafId) cancelAnimationFrame(rafId);

			rafId = requestAnimationFrame(() => onHandleMeasure(entry.contentRect.height));
		});

		observer.observe(gridElement);

		return () => {
			observer.disconnect();

			if (rafId) cancelAnimationFrame(rafId);
		};
	}, []);

	return (
		<div className="app-grid-calendar">
			<div className="grid-calendar">
				<div className="grid-calendar__calendar">
					<div className="leaves-calendar">
						<div className="leaves-calendar__header">
							<button type="button" className="leaves-calendar__button" onClick={onHandleSetToday}>
								Today
							</button>

							<div className="leaves-calendar__navigation">
								<p className="leaves-calendar__current">{formatDatePattern(date, currentDateFormat)}</p>
								<button type="button" className="leaves-calendar__button leaves-calendar__button--prev" data-target="data-prev" onClick={onHandleChangeDate}>
									<AppChevronIcon color="#0245a9" />
								</button>
								<button type="button" className="leaves-calendar__button leaves-calendar__button--next" data-target="data-next" onClick={onHandleChangeDate}>
									<AppChevronIcon color="#0245a9" />
								</button>
							</div>

							<AppButton className="leaves-calendar__help-icon" type="button" outline icon={infoIcon} onClick={onHandleOpenInfoMenu} />

							{/* prettier-ignore */}
							<AppMultiSelectInput className="leaves-calendar__region" name="region" placeholder="All Regions" options={regionOptions} value={regions} onChange={onChangeRegion} />

							<AppButton type="button" outline label="Listing View" icon={listingIcon} onClick={() => props.onLayoutChange(props.type, date)} />

							<AppButton type="button" label="Reset Leaves" icon={resetIcon} onClick={onHandleResetLeaves} />
						</div>

						<div className="leaves-calendar__body">
							<div id="leave-calendar-grid" ref={calendarGridRef} className="leaves-calendar__calendar-grid">
								<ul className="leaves-calendar__weekdays">
									{weekdays.map((o) => (
										<li key={o.day} className="leaves-calendar__weekday-item">
											<div className="leaves-calendar__weekday">{o.day}</div>
										</li>
									))}
								</ul>

								<ul className="leaves-calendar__list">
									{dates.map((a) => {
										const employeeLeaveApplied = getEmployeeLeaveApplied(a.iso);
										const dateClassName = classNames({ "leaves-calendar__date": true, "leaves-calendar__date--leave": employeeLeaveApplied && !a.isToday, "leaves-calendar__date--today": a.isToday });
										const daysClassName = classNames({
											"leaves-calendar__holiday-event": true,
											"leaves-calendar__text": true,
											"leaves-calendar__text--leave": employeeLeaveApplied && !a.isToday,
											"leaves-calendar__text--today": a.isToday,
										});
										const holidayDate = holiday[a.iso];

										const holidayEvent = holidayDate ? `${holidayDate.title}` : "";

										const onClick = (event) => {
											onHandleShowEmployeeAppliedLeave(event, employeeLeaveApplied);

											onHandleShowHolidayList(event, holidayEvent);
										};

										if (a.isNextMonth || a.isPreviousMonth) {
											return <li key={a.iso} className="leaves-calendar__item leaves-calendar__item--other-month" />;
										}

										return (
											<li className={`leaves-calendar__item ${[0, 6].includes(new Date(a.iso).getDay()) ? "is-weekend" : ""}`} key={a.iso} data-type={a.iso} onClick={onClick}>
												<div className="leaves-calendar__dates">
													<p className={dateClassName}>{a.date}</p>

													<div className="leaves-calendar__leave-rows">
														{(() => {
															const leaveBlocksForDay = [];

															for (const [combinedKey, groups] of Object.entries(holidayAndEmployeeConsecutiveMap)) {
																const [name, type, timeOffType] = combinedKey.split("|||");

																groups.forEach((group, groupIndex) => {
																	if (!group.includes(a.iso)) return;

																	let position = "middle";
																	if (group.length === 1) position = "single";
																	else if (group[0] === a.iso) position = "start";
																	else if (group[group.length - 1] === a.iso) position = "end";

																	const key = `${name}::${type}::${timeOffType}::${groupIndex}`;
																	const row = leaveBlockToRowMap[`${name}|||${type}|||${timeOffType}::${groupIndex}`];

																	leaveBlocksForDay.push({
																		name,
																		type,
																		timeOffType,
																		position,
																		row,
																		key,
																	});
																});
															}

															// Sort to maintain consistent vertical alignment
															leaveBlocksForDay.sort((a, b) => a.row - b.row);

															const maxRowsToShow = 2;
															const visibleBlocks = leaveBlocksForDay.filter((b) => b.row < maxRowsToShow);
															const hiddenCount = leaveBlocksForDay.length - visibleBlocks.length;
															a.hiddenLeaveCount = hiddenCount;

															return (
																<>
																	{visibleBlocks.map(({ name, type, position, row, key, timeOffType }, i) => {
																		const isHoliday = type === "holiday";

																		if (isHoliday) {
																			return (
																				<p key={i} className={daysClassName}>
																					<span className="leaves-calendar__holiday">{name}</span>
																				</p>
																			);
																		}

																		const icon = leaveIcons[type]?.[timeOffType] || leaveIcons["Other Leave"]?.[timeOffType];
																		const showLabel = position === "start" || position === "single";

																		return (
																			<div key={key} className={`leaves-calendar__leave-event leaves-calendar__text ${position}`} style={{ gridRow: row + 1 }}>
																				<span className="leaves-calendar__employeeLeave">
																					<img className="leaves-calendar__leave-icon" src={icon} alt="" style={{ visibility: showLabel ? "visible" : "hidden" }} />
																				
																					{showLabel ? name : "\u00A0"}
																				</span>
																			</div>
																		);
																	})}
																</>
															);
														})()}
													</div>
												</div>

												{a.hiddenLeaveCount > 0 && <p className="leaves-calendar__more">+{a.hiddenLeaveCount} more</p>}
											</li>
										);
									})}
								</ul>
							</div>

							<ul id="leaves-calendar-leaves" ref={leavesListRef} className="leaves-calendar__leaves">
								{(selectedHoliday?.[0]?.holidays || selectedHoliday)?.map((o, i) => (
									<li className="leaves-calendar__on-leave" key={`holiday-${i}`}>
										<div className="leaves-calendar__profile" style={{ backgroundImage: `url(${holidayIcon})` }} />
										<p className="leaves-calendar__on-leave-name">
											{o.title}
											<br />
											<span className="leaves-calendar__on-leave-type">Public Holiday</span>
										</p>
										<p className="leaves-calendar__on-leave-date">{getHolidayDateLabel(o)}</p>
									</li>
								))}

								{onLeaves?.map((leave, i) => {
									const icon = leaveIcons[leave.leaveTypeMapped]?.[leave.timeOffType] || leaveIcons["Other Leave"]?.[leave.timeOffType];
									const profileStyles = { backgroundImage: `url(${icon})` };

									let dates = () => {
										const firstDate = leave.ranges[0]?.date;
										if (leave.ranges.length > 1) {
											const lastDate = leave.ranges[leave.ranges.length - 1].date;
											return `${new Date(firstDate).getDate()}${nthDate(new Date(firstDate).getDate())} - ${new Date(lastDate).getDate()}${nthDate(new Date(lastDate).getDate())}`;
										} else {
											return `${new Date(leave.ranges[0].date).getDate()}${nthDate(new Date(leave.ranges[0].date).getDate())}`;
										}
									};

									return (
										<li className="leaves-calendar__on-leave" key={i}>
											<div className="leaves-calendar__profile leaves-calendar__profile--leave-icon" style={profileStyles} />
											<p className="leaves-calendar__on-leave-name">
												{leave.userFullName}
												<br />
												<span className="leaves-calendar__on-leave-type">{leave.leaveTypeMapped}</span>
											</p>
											<p className="leaves-calendar__on-leave-date">{dates()}</p>
										</li>
									);
								})}
							</ul>
						</div>
					</div>
				</div>
			</div>

			<Menu classes={{ root: "app-header-menu" }} anchorEl={anchorE2} open={!!anchorE2} onClose={onhandleCloseInfoMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
				{leaveMenuItems.map(({ type, timeOff, label, custom, className }) => (
					<MenuItem key={`${type || label}-${timeOff || "custom"}`}>
						{custom ? <div className={`circle-icon ${className}`} /> : <img className="leave-icon" src={leaveIcons[type]?.[timeOff]} alt="" />}
						<p className="app-header-menu__text">{label}</p>
					</MenuItem>
				))}
			</Menu>

			<AppRefreshLeaveModal ref={refreshLeaveRef} />
		</div>
	);
};

export default AppGridCalendar;
